// Put this line back after using vscode organizeImports
import type { Icon } from 'shared/utils'

import { COLOR } from 'app-v2/design-tokens'
import BlazonSticker from 'app/common/blazon/sticker/BlazonSticker'
import { Picture } from 'app/common/picture'
import { fonts } from 'app/common/typography'
import React, { Fragment, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { colors as oldColors } from 'shared/assets/styles'
import {
    couponCategoryCode,
    getStatusProps,
    isDefined,
    maxLength,
    splitString
} from 'shared/utils'
import styled from 'styled-components'
import { ZoomModal } from './zoom-modal'

const { red, yellow } = oldColors
const { flameRegular, roboto } = fonts
const { KINGDOM } = couponCategoryCode

type Props = {
    ...Icon,
    label: string,
    description: string,
    status: 'BURNED' | 'EXPIRED' | 'VALID',
    category: 'KINGDOM' | 'NOW',
    endDate: string,
    qrCodeImage?: string,
    imageUrl: Array<string>,
    restaurants: Array<string>,
    id: string,
    selectCoupon?: () => void,
    promotionId?: string,
    blazonProfileImageUrl?: string
}

export const Coupon = ({
    id,
    description,
    color = COLOR.PRIMARY,
    background = 'white',
    // $FlowFixMe
    size = '100%',
    className = 'coupon',
    imageUrl,
    label,
    status,
    endDate,
    restaurants,
    selectCoupon,
    blazonProfileImageUrl,
    category
}: Props) => {
    const intl = useIntl()

    const [showZoomModal, setShowZoomModal] = useState(false)

    const kingdom = category === KINGDOM
    const backgroundColor = kingdom ? 'url(#linear)' : background
    const textColor = kingdom ? 'white' : color
    const restaurantsLength = restaurants && restaurants.length
    const { opacity, enable } = getStatusProps(status)
    const splitText = label && label.length > maxLength.coupon
    let startLabel = label
    let endLabel = ''
    if (splitText) {
        startLabel = splitString(label, maxLength.coupon).startLabel
        endLabel = splitString(label, maxLength.coupon).endLabel
    }
    let startEnableMessage = ''
    let endEnableMessage = ''
    if (!enable) {
        const enableMessage = intl.formatMessage({
            id: `kingdom.privileges.coupon.stamp.${status}`
        })
        startEnableMessage = splitString(enableMessage, maxLength.coupon / 2)
            .startLabel
        endEnableMessage = splitString(enableMessage, maxLength.coupon / 2)
            .endLabel
    }
    const hasId = isDefined(id) && id !== ''

    const toggleModal = () => {
        setShowZoomModal(!showZoomModal)
    }

    return (
        <div
            onClick={() => {
                if (!!selectCoupon) {
                    //$FlowFixMe
                    selectCoupon()
                } else {
                    hasId && toggleModal()
                }
            }}>
            <Wrapper hasId={hasId}>
                <svg
                    width={size}
                    height={size}
                    className={className}
                    viewBox="0 0 170 195">
                    <g fill="none" fillRule="evenodd" opacity={opacity}>
                        <path
                            fill={backgroundColor}
                            d="M156 0c5.523 0 10 4.477 10 10v106a7 7 0 000 14v51c0 5.523-4.477 10-10 10H10c-5.523 0-10-4.477-10-10v-51a7 7 0 000-14V10C0 4.477 4.477 0 10 0h146z"
                            id="couponKINGDOM_svg__b"
                        />
                        <linearGradient
                            x1="100%"
                            y1="52.799%"
                            x2="0%"
                            y2="52.799%"
                            id="linear">
                            <stop stopColor="#4A4A52" offset="0%" />
                            <stop stopColor="#131517" offset="100%" />
                        </linearGradient>
                        <g fill="none" fillRule="evenodd">
                            <g transform="translate(-3 -3)">
                                <path
                                    d="M3 167h166v17c0 5.523-4.477 10-10 10H13c-5.523 0-10-4.477-10-10v-17z"
                                    fill={yellow}
                                    fillRule="evenodd"
                                />
                            </g>
                            {kingdom && (
                                <Fragment>
                                    <path
                                        d="M142.034 23.04l-.126-7.997 4.145 3.16 3.702-5.152 3.62 5.37 3.707-3.049.126 7.576s-6.906-3.162-15.174.092"
                                        fill={yellow}
                                    />
                                    <path
                                        d="M142.246 23.8s6.558-3.193 14.796.004c0 0-6.89 4.343-14.796-.005"
                                        fill={yellow}
                                    />
                                </Fragment>
                            )}
                            <text
                                fill={textColor}
                                fontFamily={flameRegular}
                                fontSize={14}
                                x={12}
                                y={101}>
                                {startLabel.toString()}
                            </text>
                            {splitText && (
                                <text
                                    fill={textColor}
                                    fontFamily={flameRegular}
                                    fontSize={14}
                                    x={12}
                                    y={114}>
                                    {endLabel.toString()}
                                </text>
                            )}
                            <path
                                stroke="#DADADA"
                                strokeLinecap="round"
                                strokeDasharray={3}
                                d="M12.5 125.5h145"
                            />
                            {endDate && (
                                <FormattedMessage
                                    id="kingdom.privileges.coupon.validity.label"
                                    values={{ date: endDate }}>
                                    {(text: string) => (
                                        <text
                                            fill={textColor}
                                            fontFamily={roboto}
                                            fontSize={9}
                                            x={35}
                                            y={144}>
                                            {text}
                                        </text>
                                    )}
                                </FormattedMessage>
                            )}
                            {restaurantsLength > 0 && (
                                <Fragment>
                                    <g fill={textColor}>
                                        <path
                                            d="M25 161.55a5.55 5.55 0 100-11.1 5.55 5.55 0 000 11.1zm0 .45a6 6 0 110-12 6 6 0 010 12z"
                                            fillRule="nonzero"
                                        />
                                        <path d="M25 152.55c1.489 0 2.7 1.211 2.7 2.7 0 1.397-2.212 3.891-2.464 4.171a.318.318 0 01-.472 0c-.252-.28-2.464-2.774-2.464-4.171 0-1.489 1.211-2.7 2.7-2.7zm0 1.268a1.17 1.17 0 00-1.17 1.17 1.17 1.17 0 002.339 0 1.17 1.17 0 00-1.169-1.17z" />
                                    </g>
                                    {restaurantsLength > 1 ? (
                                        <FormattedMessage id="kingdom.privileges.coupon.restaurant.label">
                                            {(text: string) => (
                                                <text
                                                    fill={textColor}
                                                    fontFamily={roboto}
                                                    fontSize={7}
                                                    x={35}
                                                    y={159}>
                                                    {text}
                                                </text>
                                            )}
                                        </FormattedMessage>
                                    ) : (
                                        <text
                                            fill={textColor}
                                            fontFamily={roboto}
                                            fontSize={9}
                                            x={35}
                                            y={159}>
                                            {restaurants[0]}
                                        </text>
                                    )}
                                </Fragment>
                            )}
                            <g transform="translate(0 -3)" fill="white">
                                {!!selectCoupon ? (
                                    <FormattedMessage id="kingdom.privilege.select.coupon">
                                        {(text: string) => (
                                            <text
                                                fontFamily={flameRegular}
                                                fontSize={12}
                                                x={42}
                                                y={185}>
                                                {text.toString().toUpperCase()}
                                            </text>
                                        )}
                                    </FormattedMessage>
                                ) : (
                                    hasId && (
                                        <g>
                                            <FormattedMessage id="kingdom.privilege.see.coupon">
                                                {(text: string) => (
                                                    <text
                                                        fontFamily={
                                                            flameRegular
                                                        }
                                                        fontSize={12}
                                                        x={45}
                                                        y={185}>
                                                        {text
                                                            .toString()
                                                            .toUpperCase()}
                                                    </text>
                                                )}
                                            </FormattedMessage>
                                        </g>
                                    )
                                )}
                            </g>
                        </g>
                    </g>
                    {!enable && !!startEnableMessage && (
                        <text
                            fill={kingdom ? 'white' : red}
                            fontFamily={flameRegular}
                            fontSize={25}
                            x={25}
                            y={55}
                            fontWeight="400">
                            {startEnableMessage.toString()}
                        </text>
                    )}
                    {!enable && !!endEnableMessage && (
                        <text
                            fill={kingdom ? 'white' : red}
                            fontFamily={flameRegular}
                            fontSize={25}
                            x={25}
                            y={80}
                            fontWeight="400">
                            {endEnableMessage.toString()}
                        </text>
                    )}
                </svg>
                {!!blazonProfileImageUrl && (
                    <BlazonStickerContainer>
                        <BlazonSticker
                            imageUrl={blazonProfileImageUrl}
                            size="small"
                        />
                    </BlazonStickerContainer>
                )}
                <Image enable={enable} opacity={opacity}>
                    <Picture singleImage imageUrl={imageUrl} />
                </Image>
            </Wrapper>
            {showZoomModal && (
                <ZoomModal
                    isOpen={showZoomModal}
                    endDate={endDate}
                    id={id}
                    title={label}
                    legalMentions={description}
                    restaurants={restaurants}
                    handleClose={toggleModal}
                    className="coupons-modal"
                    blazonProfileImageUrl={blazonProfileImageUrl}
                />
            )}
        </div>
    )
}

const Wrapper = styled.div`
    position: relative;
    ${props => props.hasId && 'cursor: pointer'};
    filter: drop-shadow(0 2px 4px rgba(48, 49, 51, 0.3));
    height: 200px;
    width: 160px;
    margin-bottom: 10px;
`

const BlazonStickerContainer = styled.div`
    position: absolute;
    top: 4%;
    left: 5%;
`

const Image = styled.div`
    position: absolute;
    ${props => !props.enable && 'z-index: -1'};
    /* Value calculated from wireframes */
    width: 50%;
    top: 25%;
    left: 50%;
    opacity: ${props => props.opacity};
    transform: translate(-50%, -48%);
`
